import { useEffect, useState, type RefObject } from "react";

interface WebkitVideo extends HTMLVideoElement {
  webkitDisplayingFullscreen?: boolean;
}

export function useIsFullscreen(ref: RefObject<HTMLElement | null>): boolean {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      const fsEl = document.fullscreenElement;
      const standard = !!fsEl && (fsEl === el || el.contains(fsEl));
      const video = el.querySelector("video") as WebkitVideo | null;
      const ios = !!video?.webkitDisplayingFullscreen;
      setIsFullscreen(standard || ios);
    };

    check();
    document.addEventListener("fullscreenchange", check);
    const video = el.querySelector("video");
    video?.addEventListener("webkitbeginfullscreen", check);
    video?.addEventListener("webkitendfullscreen", check);
    return () => {
      document.removeEventListener("fullscreenchange", check);
      video?.removeEventListener("webkitbeginfullscreen", check);
      video?.removeEventListener("webkitendfullscreen", check);
    };
  }, [ref]);

  return isFullscreen;
}

type LockableOrientation = {
  lock?: (o: "landscape" | "portrait" | "any") => Promise<void>;
  unlock?: () => void;
};

export function useLandscapeInFullscreen(isFullscreen: boolean, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const o = screen.orientation as unknown as LockableOrientation | undefined;
    if (!o?.lock) return;

    if (!isFullscreen) {
      o.unlock?.();
      return;
    }
    o.lock("landscape").catch(() => {});
    return () => {
      o.unlock?.();
    };
  }, [isFullscreen, enabled]);
}

export function useLandscapeVideo(ref: RefObject<HTMLElement | null>, enabled = true): boolean {
  const isFullscreen = useIsFullscreen(ref);
  useLandscapeInFullscreen(isFullscreen, enabled);
  return isFullscreen;
}
